import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { COIN_PRICE_CENTS, formatEuro, grossFromNet } from "@/lib/salonturbo";
import { ArrowRight, Phone, Clock, Search, Check } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import hairBob from "@/assets/hair-bob.jpg";
import stylistHands from "@/assets/stylist-hands.jpg";
import hairFade from "@/assets/hair-fade.jpg";

const FAQ = [
  {
    q: "Wie finde ich heute noch einen Friseurtermin?",
    a: "Am schnellsten geht es telefonisch: Salons in deiner Nähe wissen sofort, ob ein Stuhl frei ist. Kurzfristige Lücken entstehen oft durch Absagen, deshalb lohnt ein Anruf auch dann, wann online kein Termin mehr angezeigt wird.",
  },
  {
    q: "Zu welcher Uhrzeit sind spontan noch Termine frei?",
    a: "Häufig am späten Vormittag und am frühen Nachmittag, weil dort weniger nach der Arbeit gebucht wird. Auch Tage nach Feiertagen und regnerische Wochentage sind oft weniger ausgebucht.",
  },
  {
    q: "Gibt es Friseure ohne Termin?",
    a: "Viele Salons nehmen Laufkundschaft an, wenn gerade Zeit ist. Verlässlicher ist ein kurzer Anruf vorher – so vermeidest du Wartezeit vor Ort.",
  },
  {
    q: "Ich bin Salon: Wie werde ich bei diesen Suchen gefunden?",
    a: `Mit SalonTurbo markierst du die Tage, an denen noch Termine frei sind. Für jeden dieser Tage laufen lokale Suchanzeigen auf Begriffe wie „Friseur Termin heute“ und führen auf deine Salonseite mit Anruf-Button. Ein Tag kostet einen Coin, also ${formatEuro(COIN_PRICE_CENTS)} netto (${formatEuro(grossFromNet(COIN_PRICE_CENTS))} inkl. MwSt.).`,
  },
  {
    q: "Muss ich als Salon selbst Anzeigen verwalten?",
    a: "Nein. Die Kampagne wird einmal für dich angelegt und danach nur an den Tagen aktiviert, die du auswählst. Sie stoppt automatisch vor Ladenschluss.",
  },
];

export const Route = createFileRoute("/friseur-termin-heute")({
  head: () => ({
    meta: [
      { title: "Friseurtermin heute: spontan freie Stühle finden" },
      {
        name: "description",
        content:
          "Heute noch einen Friseurtermin bekommen: wann kurzfristig Stühle frei werden und wie du sie findest. Für Salons: freie Tage gezielt sichtbar machen.",
      },
      { property: "og:title", content: "Friseurtermin heute: spontan freie Stühle finden" },
      {
        property: "og:description",
        content:
          "Kurzfristige Termine entstehen durch Absagen. So findest du sie – und so machen Salons ihre freien Tage sichtbar.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
    ],
  }),
  component: TerminHeute,
});

function TerminHeute() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <section className="mx-auto max-w-5xl px-4 pb-12 pt-12 sm:pt-16">
          <p className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 eyebrow">
            <Clock className="size-3.5" /> Kurzfristige Termine
          </p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-light leading-[1.1] sm:text-5xl">
            Friseurtermin heute: So findest du spontan einen freien Stuhl
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Kurzfristige Termine entstehen fast immer durch Absagen – und die tauchen selten in
            Online-Kalendern auf. Wer weiß, wo und wann er fragt, bekommt deutlich häufiger noch
            heute einen Platz.
          </p>
        </section>

        <section className="mx-auto max-w-5xl px-4 pb-14">
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { src: hairBob, alt: "Frisch geschnittener Bob" },
              { src: stylistHands, alt: "Friseurin schneidet Haare mit der Schere" },
              { src: hairFade, alt: "Moderner Fade-Haarschnitt" },
            ].map((img) => (
              <div key={img.alt} className="overflow-hidden rounded-3xl shadow-soft">
                <img
                  src={img.src}
                  alt={img.alt}
                  width={1024}
                  height={1280}
                  loading="lazy"
                  className="h-56 w-full object-cover sm:h-72"
                />
              </div>
            ))}
          </div>
        </section>

        <section className="border-y bg-secondary/50">
          <div className="mx-auto max-w-4xl px-4 py-16">
            <h2 className="font-display text-2xl font-normal sm:text-4xl">
              Vier Wege zum kurzfristigen Termin
            </h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              {[
                {
                  icon: Phone,
                  title: "Direkt anrufen",
                  text: "Der Salon sieht seinen Kalender in Echtzeit – inklusive der Absage von vor fünf Minuten. Online-Buchungssysteme zeigen solche Lücken oft nicht.",
                },
                {
                  icon: Clock,
                  title: "Randzeiten nutzen",
                  text: "Späterer Vormittag und früher Nachmittag sind meist weniger gefragt als der Feierabend.",
                },
                {
                  icon: Search,
                  title: "Nach Salons in der Nähe suchen",
                  text: "Ein Salon zwei Straßen weiter hat oft frei, während dein Stammsalon ausgebucht ist. Kurze Wege erhöhen die Trefferquote.",
                },
                {
                  icon: Check,
                  title: "Flexibel bleiben",
                  text: "Wenn du beim Termin und beim Stylisten flexibel bist, findest sich häufiger ein Platz am selben Tag.",
                },
              ].map((item) => (
                <div key={item.title} className="rounded-2xl border bg-card p-6">
                  <item.icon className="size-6 text-primary" />
                  <h3 className="mt-3 font-display text-lg font-medium">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-4 py-16">
          <p className="eyebrow">
            Für Salonbetreiber
          </p>
          <h2 className="mt-3 font-display text-2xl font-normal sm:text-4xl">
            Du bist Salon? Genau diese Suchen kannst du gezielt einsammeln
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Jeden Tag suchen Menschen aktiv nach einem Termin für heute oder morgen. Mit SalonTurbo
            markierst du im Kalender die Tage, an denen bei dir noch Platz ist. Nur für diese Tage
            laufen lokale Suchanzeigen – automatisch gestartet und vor Ladenschluss wieder beendet.
          </p>
          <ul className="mt-6 space-y-3 text-sm sm:text-base">
            {[
              `Ein Coin = ein geboosteter Tag = ${formatEuro(COIN_PRICE_CENTS)} netto.`,
              "Keine eigene Anzeigenverwaltung, keine Agentur, keine Laufzeit.",
              "An Tagen ohne Boost läuft keine Werbung und es entstehen keine Kosten.",
            ].map((item) => (
              <li key={item} className="flex gap-2">
                <Check className="mt-0.5 size-4 shrink-0 text-success" />
                <span className="text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg">
              <Link to="/auth">
                Salon einrichten <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/friseur-werbung">Mehr zu Friseur-Werbung</Link>
            </Button>
          </div>
        </section>

        <section className="border-y bg-secondary/50">
          <div className="mx-auto max-w-4xl px-4 py-16">
            <h2 className="font-display text-2xl font-normal sm:text-3xl">
              Deine eigene Salonseite ist inklusive
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              SalonTurbo erstellt für jeden Salon eine eigene Seite mit Bildern, Adresse,
              Öffnungszeiten und einem prominenten Anruf-Button. An geboosteten Tagen erscheint dort
              zusätzlich der Hinweis, dass kurzfristig Termine frei sind – genau das, wonach spontane
              Kunden suchen.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 py-16">
          <h2 className="font-display text-2xl font-normal sm:text-4xl">Häufige Fragen</h2>
          <Accordion type="single" collapsible className="mt-6">
            {FAQ.map((item, index) => (
              <AccordionItem key={item.q} value={`faq-${index}`}>
                <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
